import React, { useEffect, useState } from "react";
import { Row, Col, Button, Input, Label } from "reactstrap";
import { useNavigate } from "react-router-dom";
import axiosConfig from "../../AxiosConfig";
import { AgGridReact } from "ag-grid-react";
import { Trash2, Edit } from "lucide-react";
import { AiOutlineDownload } from "react-icons/ai";

export default function SalesInvoice() {
  const navigate = useNavigate();

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const currentYear = new Date().getFullYear();

  const years = Array.from(
    { length: 10 },
    (_, i) => currentYear - i
  );
  const [filterType, setFilterType] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [pinnedRow, setPinnedRow] = useState([]);

  const userData = JSON.parse(localStorage.getItem("userData"));

  // ✅ Checkbox handler
  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id]
    );
  };

  // ✅ ColumnDefs (FULL)
  const columnDefs = [
    {
      headerName: "Actions",
      width: 120,

      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },

      cellRenderer: (params) => {
        if (!params?.data?.invoiceId) return "-";

        const id = params?.data?._id;
        const isChecked = selectedIds.includes(id);

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              width: "100%",
              height: "100%",
              paddingRight:"40px"
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => handleCheckboxChange(id)}
            />

            <Trash2
              size={18}
              color="red"
              style={{ cursor: "pointer" }}
            />

            <Edit
              size={18}
              color="blue"
              style={{ cursor: "pointer" }}
            />
          </div>
        );
      },
    },

    {
      headerName: "Status",
      field: "status",
      width: 120,
    },

    // {
    //   headerName: "Payment",
    //   width: 130,
    //   cellRenderer: (params) => {
    //     if (params.node?.rowPinned) return null;

    //     return params.data?.paymentVerified ? (
    //       <span style={{ color: "green" }}>Paid</span>
    //     ) : (
    //       <button
    //         style={{
    //           background: "#28a745",
    //           color: "#fff",
    //           border: "none",
    //           padding: "2px 8px",
    //           fontSize: "11px",
    //           borderRadius: "4px",
    //         }}
    //       >
    //         Pay Now
    //       </button>
    //     );
    //   },
    // },

    {
      headerName: "Invoice",
      width: 90,
      cellRenderer: (params) => {
        if (!params?.data?.invoiceId) return "-";

        return (
          <AiOutlineDownload size={16} color="blue" />
        );
      },
    },

    {
      headerName: "Invoice No.",
      field: "invoiceId",
      width: 130,
      valueGetter: (params) => {
        if (!params?.data?.invoiceId) return "Total";

        const companyDetails = JSON.parse(
          localStorage.getItem("Companydetail")
        ) || {};

        return `${companyDetails.Prefix || ""}${params.data.invoiceId}${companyDetails.Suffix || ""}`;
      },
    },

    {
      headerName: "Date",
      field: "date",
      width: 120,
      valueGetter: (p) => p.data?.date?.split("T")[0],
    },

    {
      headerName: "Party Name",
      field: "partyId.CompanyName",
      width: 220,
      valueGetter: (p) => p.data?.partyId?.CompanyName,
    },

    {
      headerName: "Party Limit",
      field: "partyId.limit",
      width: 120,
      valueGetter: (p) => p.data?.partyId?.limit,
    },

    {
      headerName: "Amount",
      field: "amount",
      width: 110,
    },

    {
      headerName: "IGST",
      field: "igstTotal",
      width: 110,
      valueGetter: (p) => Number(p.data?.igstTotal || 0).toFixed(2),
    },

    {
      headerName: "CGST",
      field: "cgstTotal",
      width: 100,
      valueGetter: (p) => Number(p.data?.cgstTotal || 0).toFixed(2),
    },

    {
      headerName: "SGST",
      field: "sgstTotal",
      width: 100,
      valueGetter: (p) => Number(p.data?.sgstTotal || 0).toFixed(2),
    },

    {
      headerName: "Round Off",
      field: "roundOff",
      width: 100,
    },

    {
      headerName: "Grand Total",
      field: "grandTotal",
      width: 120,
      valueGetter: (p) => Number(p.data?.grandTotal || 0).toFixed(2),
    },
  ];

  // ✅ API CALL
  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await axiosConfig.get(
        `/order/view-complete-order/${userData?.database}`
      );

      console.log("SALES INVOICE API 👉", res?.data);

      let data = res?.data?.orderHistory || [];

      // Deactive remove
      data = data.filter(
        (d) => d?.status?.toLowerCase() !== "deactive"
      );

      // Only completed
      data = data.filter(
        (d) => d?.status?.toLowerCase() === "completed"
      );

      // Registered / Unregistered
      if (filterType === "registered") {
        data = data.filter(
          (d) =>
            d?.partyId?.registrationType === "Regular" ||
            d?.partyId?.registrationType === "Register"
        );
      }

      if (filterType === "unregistered") {
        data = data.filter(
          (d) =>
            d?.partyId?.registrationType === "UnKnown" ||
            d?.partyId?.registrationType === "Unregister"
        );
      }

      // Year + Month
      data = data.filter((d) => {
        if (!d?.date) return false;

        const dt = new Date(d.date);

        const apiYear = dt.getFullYear();

        const apiMonth = dt.toLocaleString("en-US", {
          month: "long",
        });

        return (
          apiYear === Number(year) &&
          apiMonth === month
        );
      });

      console.log("FILTERED DATA 👉", data);

      // Latest first
      data.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      // =========================
      // TOTAL
      // =========================

      let total = {
        amount: 0,
        igst: 0,
        cgst: 0,
        sgst: 0,
        round: 0,
        grand: 0,
      };

      data.forEach((item) => {
        total.amount += Number(item?.amount || 0);
        total.igst += Number(item?.igstTotal || 0);
        total.cgst += Number(item?.cgstTotal || 0);
        total.sgst += Number(item?.sgstTotal || 0);
        total.round += Number(item?.roundOff || 0);
        total.grand += Number(item?.grandTotal || 0);
      });

      setPinnedRow([
        {
          invoiceId: "Total",
          amount: total.amount.toFixed(2),
          igstTotal: total.igst.toFixed(2),
          cgstTotal: total.cgst.toFixed(2),
          sgstTotal: total.sgst.toFixed(2),
          roundOff: total.round.toFixed(2),
          grandTotal: total.grand.toFixed(2),
        },
      ]);

      setRowData(data);

    } catch (err) {
      console.log("Sales Invoice Error 👉", err);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Year ya Month select nahi hai
    if (!year || !month) {
      setRowData([]);
      setPinnedRow([]);
      return;
    }

    fetchData();
  }, [year, month, filterType]);

  return (
    <div className="erp-page-grid">

      {/* 🔥 HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="mb-0">Sales Invoice List</h4>

        <div className="d-flex gap-2 align-items-center">

          {/* Year */}
          <Input
            bsSize="sm"
            type="select"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            style={{ width: "100px", height: "30px" }}
          >
            <option value="">Year</option>

            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Input>

          {/* Month */}
          <Input
            bsSize="sm"
            type="select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ width: "130px", height: "30px" }}
          >
            <option value="">Month</option>

            {[
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
              "January",
              "February",
              "March",
            ].map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Input>

          {/* Buttons */}
          <Button
            size="sm"
            color="secondary"
            disabled={!year || !month}
            onClick={() => setFilterType("all")}
          >
            All
          </Button>

          <Button
            size="sm"
            color="success"
            disabled={!year || !month}
            onClick={() => setFilterType("registered")}
          >
            Reg
          </Button>

          <Button
            size="sm"
            color="warning"
            disabled={!year || !month}
            onClick={() => setFilterType("unregistered")}
          >
            Unreg
          </Button>

          <Button
            size="sm"
            color="primary"
            onClick={() => navigate("/sales-and-purchases?view=create-sales-invoice")}
          >
            + Invoice
          </Button>
        </div>
      </div>

      {/* 🔥 GRID */}
      <div className="ag-theme-alpine" style={{ height: 500 }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          pinnedBottomRowData={pinnedRow}
          pagination={true}
        />
      </div>
    </div>
  );
}